import React, { FC, useState } from "react";
import {
  Checkbox,
  Divider,
  FormControlLabel,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import TableChartIcon from "@mui/icons-material/TableChart";
import TextSnippetIcon from "@mui/icons-material/TextSnippet";
import { useTranslation } from "react-i18next";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import DownloadIcon from "@mui/icons-material/Download";
import { api } from "@/helper/api";
import { useConfirmationModal } from "@/context/confirmationModalContext";

export interface ExportMenuItemConfig {
  label: string; // translation key
  format: "csv" | "xlsx";
  lang?: "de" | "en";
  icon?: "text" | "chart";
}

export type ExportMenuItem = ExportMenuItemConfig | { isSeparator: true };

export interface ExportMenuCheckboxOption {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

interface ExportMenuProps {
  handleError: (error: any) => void;
  exportParameter:
    | "export" //enrolment
    | "export_subject_info_set"
    | "export_xlsx"
    | "export_multiple_records" //accidentBook
    | "export_preventive_care_registry" //preventiveCare
    | "export_definitions"; //measurementDefinition
  entity: string;
  entityId?: string;
  subjectId?: string; //accidentBook
  handleNotification?: (
    variant: "success" | "error" | "warning" | "info",
    message: string,
    success: boolean,
  ) => void;
  parametersToRender?: Record<string, any>; //enrolment
  shouldNotImpersonate?: boolean; //enrolment

  showAdditionalAnchordataCheckbox?: boolean;
  additionalCheckboxOption?: ExportMenuCheckboxOption;
  getConfirmationMessage?: (format: "csv" | "xlsx") => string;
  menuItems: ExportMenuItem[];
  buttonClassname?: string;
  buttonLabel?: string;
}

const ExportMenu: FC<ExportMenuProps> = ({
  handleError,
  exportParameter,
  entity,
  entityId,
  subjectId,
  handleNotification,
  showAdditionalAnchordataCheckbox,
  additionalCheckboxOption,
  getConfirmationMessage,
  menuItems,
  buttonClassname,
  buttonLabel = "export",
  parametersToRender,
  shouldNotImpersonate,
}) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [filterMode, setFilterMode] = useState<
    "parents_only" | "include_inherited_values" | null
  >(null);

  const open = Boolean(anchorEl);
  const { showConfirmationModalAsync } = useConfirmationModal();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const getConfirmation = (format: "csv" | "xlsx") => {
    if (getConfirmationMessage) return getConfirmationMessage(format);
    if (filterMode === "parents_only")
      return t("confirm_export_data_parents_only", { fileformat: format });
    if (filterMode === "include_inherited_values")
      return t("confirm_export_data_inherited_values", { fileformat: format });
    return t("confirm_export_data", { fileformat: format });
  };

  const handleExport = async (format: "csv" | "xlsx", lang?: "de" | "en") => {
    const confirmExport = await showConfirmationModalAsync(
      getConfirmation(format),
    );

    if (!confirmExport) {
      return;
    }
    const additionalUrlParameter: any = {
      ...parametersToRender,
      file_format: format,
    };

    if (lang) {
      additionalUrlParameter["header_language"] = lang;
    }
    if (subjectId) {
      additionalUrlParameter["company_subject_id"] = subjectId;
    }

    if (showAdditionalAnchordataCheckbox) {
      if (filterMode === "parents_only") {
        additionalUrlParameter["parents_only"] = true;
      }
      if (filterMode === "include_inherited_values") {
        additionalUrlParameter["include_inherited_values"] = true;
      }
    }

    const additionalRoutePart: any = {};
    if (exportParameter) {
      additionalRoutePart[exportParameter] = "/";
    }

    try {
      const res: any = await new Promise((resolve, reject) => {
        api.genericApiRequest({
          method: "get",
          entity: entity,
          entityId: entityId,
          shouldNotImpersonate,
          responseType: "blob",
          additionalRouteParts: additionalRoutePart,
          parametersToRender: additionalUrlParameter,
          successHandler: (res: any) => {
            resolve(res);
          },
          failHandler: (error: any) => {
            reject(error);
          },
        });
      });

      const contentType = res.headers["content-type"];
      const blob = new Blob([res.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");

      const disposition = res.headers["content-disposition"];
      const match = disposition?.match(/filename="?([^"]+)"?/);
      let filename = match?.[1];

      if (!filename) {
        filename = `${entity}_export.${format}`;
      }

      link.href = url;
      link.download = filename;

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);

      handleClose();
      handleNotification?.("success", t("download_successful"), true);
    } catch (error) {
      handleError(error);
    }
  };

  const getIcon = (iconType: ExportMenuItemConfig["icon"]) => {
    switch (iconType) {
      case "chart":
        return <TableChartIcon fontSize="small" />;
      case "text":
      default:
        return <TextSnippetIcon fontSize="small" />;
    }
  };

  return (
    <>
      <Tooltip placement="right" title={t("export_options") as string}>
        <GenericButton
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={handleClick}
          className={buttonClassname}
        >
          {t(buttonLabel)}
        </GenericButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {additionalCheckboxOption && [
          <MenuItem
            key="additional-checkbox-option"
            onClick={(event) => event.stopPropagation()}
            disableRipple
            sx={{
              backgroundColor: "transparent !important",
              "&:hover": {
                backgroundColor: "transparent !important",
                cursor: "default",
              },
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={additionalCheckboxOption.checked}
                  onChange={(_, checked) =>
                    additionalCheckboxOption.onChange(checked)
                  }
                  size="small"
                  sx={{ padding: 0, marginRight: "1rem" }}
                />
              }
              label={t(additionalCheckboxOption.label)}
              sx={{ margin: 0 }}
            />
          </MenuItem>,
          <Divider key="additional-checkbox-option-divider" />,
        ]}

        {showAdditionalAnchordataCheckbox && (
          <MenuItem
            onClick={(e) => e.stopPropagation()}
            disableRipple
            sx={{
              justifyContent: "center",
              backgroundColor: "transparent !important",

              "&:hover": {
                backgroundColor: "transparent !important",
                cursor: "default",
              },
            }}
          >
            <ToggleButtonGroup
              value={filterMode}
              exclusive
              onChange={(e, newMode) => setFilterMode(newMode)}
              size="small"
              color="primary"
              fullWidth
              sx={{
                "& .MuiToggleButtonGroup-grouped:first-of-type": {
                  borderTopLeftRadius: "0.5rem",
                  borderBottomLeftRadius: "0.5rem",
                },
                "& .MuiToggleButtonGroup-grouped:last-of-type": {
                  borderTopRightRadius: "0.5rem",
                  borderBottomRightRadius: "0.5rem",
                },

                "& .Mui-selected": {
                  color: "#8c1ec8 !important",
                  backgroundColor: "#d6bce9 !important",
                },

                "& .Mui-selected:hover": {
                  backgroundColor: "#d6bce9 !important",
                  opacity: 0.9,
                },
              }}
            >
              <ToggleButton value="parents_only" disableRipple>
                {t("parents_only")}
              </ToggleButton>
              <ToggleButton value="include_inherited_values" disableRipple>
                {t("include_inherited_values")}
              </ToggleButton>
            </ToggleButtonGroup>
          </MenuItem>
        )}

        {/* {showParentsOnlyCheckbox && (
          <hr style={{ border: "0.5px solid #eee", margin: "4px 0" }} />
        )} */}

        {menuItems.map((item, index) => {
          if ("isSeparator" in item) {
            return (
              <hr
                key={index}
                style={{ border: "0.5px solid #eee", margin: "4px 0" }}
              />
            );
          } else {
            return (
              <MenuItem
                key={index}
                onClick={() => handleExport(item.format, item.lang)}
              >
                <ListItemIcon>{getIcon(item?.icon)}</ListItemIcon>
                <ListItemText>
                  {t(item.label, { fileformat: item.format })}
                </ListItemText>
              </MenuItem>
            );
          }
        })}
      </Menu>
    </>
  );
};
export default ExportMenu;
